/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useEffect, useCallback } from 'react'
// Third-party
import { CloseOutlined } from '@ant-design/icons'
import { css } from '@emotion/react'
import { Modal, Input, App } from 'antd'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
// Relative
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'

interface AddDivisionModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (divisionName: string) => Promise<void>
  loading?: boolean
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
    padding: '24px 16px',
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    padding: '0',
    backgroundColor: colors.SECONDARY_300,
  },
}

export const AddDivisionModal: React.FC<AddDivisionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
}) => {
  const [divisionName, setDivisionName] = useState('')
  const { message } = App.useApp()

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setDivisionName('')
    }
  }, [isOpen])

  const handleSubmit = useCallback(async () => {
    if (!divisionName.trim()) {
      message.error('Please enter a division name')
      return
    }

    try {
      await onSubmit(divisionName.trim())
      message.success(`Division "${divisionName}" added successfully`)
      setDivisionName('')
      onClose()
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to add division'
      message.error(errorMessage)
    }
  }, [divisionName, onSubmit, onClose, message])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !loading) {
        handleSubmit()
      }
    },
    [handleSubmit, loading]
  )

  const closeIcon = (
    <CloseOutlined
      css={css`
        color: ${colors.SOLITUDE};
        font-size: 16px;
      `}
    />
  )

  const footer = (
    <div
      css={css`
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 16px;
        padding: 16px;
      `}
    >
      <Button
        text='Cancel'
        onClick={onClose}
        variant='secondary'
        disabled={loading}
      />
      <Button
        text='Add Division'
        onClick={handleSubmit}
        variant='primary'
        isLoading={loading}
        disabled={loading || !divisionName.trim()}
      />
    </div>
  )

  return (
    <Modal
      width={480}
      closeIcon={closeIcon}
      title={
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          Add Division
        </Text>
      }
      footer={footer}
      open={isOpen}
      onCancel={onClose}
      centered
      styles={modalStyles}
      maskClosable={false}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
          Division Name
        </Text>
        <Input
          placeholder='Enter division name'
          value={divisionName}
          onChange={(e) => setDivisionName(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          css={css`
            background-color: ${colors.SECONDARY_400};
            border: 1px solid ${colors.SECONDARY_200};
            color: ${colors.WHITE};
            padding: 10px 12px;
            border-radius: 8px;

            &::placeholder {
              color: ${colors.NEUTRAL_70};
            }

            &:hover,
            &:focus {
              border-color: ${colors.PRIMARY_300};
              background-color: ${colors.SECONDARY_400};
            }

            &.ant-input:focus,
            &.ant-input-focused {
              box-shadow: 0 0 0 2px rgba(242, 180, 0, 0.2);
            }
          `}
          autoFocus
        />
      </div>
    </Modal>
  )
}

export default AddDivisionModal
